import {
  Button,
  Divider,
  Grid,
  Text,
  Toast,
  Icons,
  RadioController,
  Loader,
} from "@pushpress/shared-components";
import React, { useContext, useEffect, useMemo } from "react";
import { Control, useController, useWatch } from "react-hook-form";
import { SwitchPlanForm, getBillingIntervalType } from "./SwitchPlanForm.utils";
import {
  CurrentUserSubscriptionFragment,
  useGetInvoicePreviewForSwitchUserSubscriptionQuery,
} from "../SwitchPlanDrawer-generated.hooks";
import { extractError, formatToUSD } from "../../../utils/misc";
import { AppContext } from "../../../context/app";
import SectionTitleAndDescription from "./components/SectionTitleAndDescription";
import { formatInTimeZone } from "date-fns-tz";
import { getDiscountDescription } from "../../../components/controllers/DiscountSelect/DiscountSelect.utils";
import { CASH_BIL_UUID } from "../../../components/controllers/UserBillingSelect/UserBillingSelect.utils";

type SelectWhenToSwitchStepProps = {
  control: Control<SwitchPlanForm>;
  goToNextStep: (whenToSwitch: SwitchPlanForm["whenToSwitch"]) => void;
  goBack: VoidFunction;
  userSubscription: CurrentUserSubscriptionFragment;
};

function SelectWhenToSwitchStep({
  control,
  goToNextStep,
  goBack,
  userSubscription,
}: SelectWhenToSwitchStepProps) {
  const { client } = useContext(AppContext);
  const newPlan = useWatch({ control: control, name: "newPlan" });
  const discount = useWatch({ control: control, name: "discount" });
  const whenToSwitch = useWatch({ control: control, name: "whenToSwitch" });
  const setupFee = useWatch({ control: control, name: "setupFee" });
  const { field: whenToSwitchField } = useController({
    control,
    name: "whenToSwitch",
  });
  const planPrice = useWatch({ control: control, name: "planPrice" });

  const amount = planPrice ? planPrice?.amount : Number(newPlan?.amount);
  const intervalType = planPrice ? planPrice?.intervalType : newPlan?.intervalType;
  const interval = planPrice ? planPrice?.interval : newPlan?.interval;

  const {
    data: getInvoicePreviewForSwitchUserSubscriptionQuery,
    loading: loadingGetInvoicePreviewForSwitchUserSubscriptionQuery,
    error: getInvoicePreviewForSwitchUserSubscriptionQueryError,
  } = useGetInvoicePreviewForSwitchUserSubscriptionQuery({
    variables: {
      getInvoicePreviewForSwitchUserSubscriptionInput: {
        newPlanUuid: newPlan.planUuid,
        subscriptionUuid: userSubscription.uuid,
        billingUuid: CASH_BIL_UUID,
        discountUuid: discount?.discountUuid,
        userSubscriptionSwitchCriteria: "Now",
      },
    },
    skip: !newPlan.planUuid,
  });

  const { shouldDisableSwitchPlanNow, disablePlanSwitchNowMessage } =
    useMemo(() => {
      // We should not allow select NOW if the total is is not chargeable
      const invoicePreview =
        getInvoicePreviewForSwitchUserSubscriptionQuery?.getInvoicePreviewForSwitchUserSubscription;

      if (invoicePreview === undefined)
        return {
          shouldDisableSwitchPlanNow: false,
          disablePlanSwitchNowMessage: "",
        };

      // TODO: show a more detailed message from the API since there are at least 3 reasons why we can't switch now all returning a total < 1
      return {
        shouldDisableSwitchPlanNow: !invoicePreview?.isChargeable,
        disablePlanSwitchNowMessage:
          "Can not switch to this plan now. Try switching on the next bill date",
      };
    }, [getInvoicePreviewForSwitchUserSubscriptionQuery]);

  useEffect(() => {
    // If we are disabling the switch plan selection, the default would be the next billing cycle
    if (shouldDisableSwitchPlanNow === true) {
      whenToSwitchField.onChange("NextBillDate");
    }
  }, [shouldDisableSwitchPlanNow, whenToSwitchField]);

  if (getInvoicePreviewForSwitchUserSubscriptionQueryError) {
    return (
      <div className="mt-3 mb-3">
        <Toast
          icon={Icons.Warning}
          message={extractError(
            getInvoicePreviewForSwitchUserSubscriptionQueryError,
          )}
          iconColor="error"
        />
      </div>
    );
  }

  if (
    loadingGetInvoicePreviewForSwitchUserSubscriptionQuery ||
    !getInvoicePreviewForSwitchUserSubscriptionQuery
  ) {
    return (
      <Grid
        container
        item
        alignItems="center"
        justifyContent="center"
        xs={12}
        paddingY={4}
      >
        <Loader />
      </Grid>
    );
  }

  return (
    <>
      <Grid container>
        <SectionTitleAndDescription
          title="Current plan"
          description={`
            ${userSubscription.plan.name} (${formatToUSD(
              userSubscription.amount,
              client.currencyIso,
            )} ${getBillingIntervalType(
              userSubscription.intervalType,
              userSubscription.chargeInterval,
            )})
              `}
        />
        <Grid item xs={12}>
          <Divider vPadding={16} />
        </Grid>
        <SectionTitleAndDescription
          title="New plan"
          description={`
                ${newPlan.name} (${formatToUSD(
                  amount,
                  client.currencyIso,
                )} ${getBillingIntervalType(
                  intervalType,
                  interval,
                )})
              `}
        />
        <Grid item xs={12}>
          <Divider vPadding={16} />
        </Grid>
        {discount && (
          <>
            <SectionTitleAndDescription
              title="Discount"
              description={`
                ${discount.name} (${getDiscountDescription(
                  discount,
                  client.currencyIso,
                )})`}
            />
            <Grid item xs={12}>
              <Divider vPadding={16} />
            </Grid>
          </>
        )}
        <SectionTitleAndDescription
          title="Setup fee"
          description={formatToUSD(setupFee, client.currencyIso)}
        />
        <Grid item xs={12}>
          <Divider vPadding={16} />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12}>
          <Text
            variant="heading-5"
            color="wolf-800"
            weight="600"
            className="pb-3"
          >
            Choose when to switch
          </Text>
          <div data-cy="when-to-switch">
            <RadioController
              options={[
                {
                  label: "Now",
                  value: "Now",
                },
                {
                  label: `${formatInTimeZone(
                    new Date(userSubscription.nextCharge),
                    "UTC",
                    "MMMM d, yyyy",
                  )} (next bill date of current plan)`,
                  value: "NextBillDate",
                },
              ]}
              direction="vertical"
              control={control}
              name="whenToSwitch"
              label="When to switch"
              hideLabel
              required
              disabled={shouldDisableSwitchPlanNow}
            />
          </div>
          {whenToSwitch === "Now" && (
            <div className="mt-3 mb-3">
              <Toast
                icon={Icons.Info}
                iconColor="success"
                message="The new plan will be activated immediately; the current plan
                will be deactivated immediately."
              />
            </div>
          )}
          {shouldDisableSwitchPlanNow && (
            <div className="mt-3 mb-3">
              <Toast
                icon={Icons.Info}
                iconColor="success"
                message={disablePlanSwitchNowMessage}
              />
            </div>
          )}
        </Grid>
      </Grid>
      <Grid container item xs={12}>
        <Divider vPadding={16} />
      </Grid>
      <Grid container justifyContent="space-between">
        <Grid item>
          <Button buttonType="text" text="Back" onClick={goBack} />
        </Grid>
        <Grid item>
          <Button
            buttonType="secondary"
            text="Next"
            disabled={!whenToSwitch}
            onClick={() => goToNextStep(whenToSwitch)}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default SelectWhenToSwitchStep;
